import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, downloadFile, formatApiErrorDetail } from "../lib/api";
import DropZone from "../components/DropZone";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

export default function PdfAnnotate() {
  const { t } = useTranslation();
  const [file, setFile] = useState(null);
  const [page, setPage] = useState(1);
  const [x, setX] = useState(72);
  const [y, setY] = useState(72);
  const [text, setText] = useState("");
  const [size, setSize] = useState(14);
  const [result, setResult] = useState(null);

  async function onApply() {
    if (!file || !text.trim()) {
      toast.error(t("pdf_text_input"));
      return;
    }
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("page", page);
      fd.append("x", x);
      fd.append("y", y);
      fd.append("text", text);
      fd.append("font_size", size);
      const { data } = await api.post("/pdf/add-text", fd);
      setResult(data);
      toast.success(t("success"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-10 py-12" data-testid="pdf-annotate-page">
      <Link to="/tools/pdf" className="eyebrow text-zinc-500 hover:text-black">← {t("cta_back")}</Link>
      <h1 className="hero-h2 text-3xl sm:text-5xl mt-2">{t("tool_annotate")}</h1>
      <p className="text-zinc-500 mt-2">{t("tool_annotate_desc")}</p>

      <div className="mt-8">
        {!file ? (
          <DropZone accept="application/pdf" onFiles={(fs) => setFile(fs[0])} testId="annotate-dropzone" />
        ) : (
          <div className="border border-zinc-200 rounded-md p-6 bg-white space-y-6">
            <div>
              <span className="eyebrow">FILE</span>
              <div className="hero-h2 text-xl mt-1">{file.name}</div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="eyebrow">{t("pdf_text_input")}</Label>
                <Input value={text} onChange={(e) => setText(e.target.value)}
                  className="rounded-sm border-zinc-300 h-11 mt-1" data-testid="annotate-text" />
              </div>
              <div>
                <Label className="eyebrow">{t("pdf_place_on_page")}</Label>
                <Input type="number" value={page} onChange={(e) => setPage(parseInt(e.target.value) || 1)} min={1}
                  className="rounded-sm border-zinc-300 h-11 mt-1" data-testid="annotate-page" />
              </div>
              <div>
                <Label className="eyebrow">{t("pdf_position_x")} (pt)</Label>
                <Input type="number" value={x} onChange={(e) => setX(parseFloat(e.target.value))}
                  className="rounded-sm border-zinc-300 h-11 mt-1" data-testid="annotate-x" />
              </div>
              <div>
                <Label className="eyebrow">{t("pdf_position_y")} (pt) <span className="text-zinc-400 lowercase">(top)</span></Label>
                <Input type="number" value={y} onChange={(e) => setY(parseFloat(e.target.value))}
                  className="rounded-sm border-zinc-300 h-11 mt-1" data-testid="annotate-y" />
              </div>
              <div>
                <Label className="eyebrow">{t("pdf_font_size")}</Label>
                <Input type="number" value={size} onChange={(e) => setSize(parseInt(e.target.value) || 12)} min={6}
                  className="rounded-sm border-zinc-300 h-11 mt-1" data-testid="annotate-size" />
              </div>
            </div>

            <div className="flex gap-3">
              <Button onClick={onApply} className="rounded-sm bg-black text-white hover:bg-zinc-800 h-11" data-testid="annotate-apply">
                {t("cta_apply")} →
              </Button>
              <Button variant="ghost" className="rounded-sm h-11" onClick={() => { setFile(null); setResult(null); setText(""); }} data-testid="annotate-clear">
                {t("cta_clear")}
              </Button>
            </div>
          </div>
        )}

        {result && (
          <div className="mt-6 border border-black rounded-md p-6 bg-zinc-50 fade-up" data-testid="annotate-result">
            <span className="eyebrow">{t("success")}</span>
            <div className="hero-h2 text-xl mt-1">{result.original_filename}</div>
            <div className="mt-4">
              <Button onClick={() => downloadFile(result.id, result.original_filename)}
                className="rounded-sm bg-black text-white hover:bg-zinc-800 h-10" data-testid="annotate-download">
                ↓ {t("cta_download")}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
